import React, { useState } from 'react';
import { Alert, Button, Checkbox, Input, Select } from '../../../components/ui';
import { anchorForMonth, schedulePreset, SCHEDULE_PRESETS } from './rosterUtils';

const FIELD_OPTIONS = {
  designation: ['Manager', 'Engineer', 'Supervisor', 'Technician'],
  project: ['FM008', 'FM014', 'FM020', 'FM030']
};

export default function RosterProfileEditor({ employee, month, onSave, onDirtyChange, busy }) {
  const [form, setForm] = useState(() => employee.profile || {
    name: employee.username, designation: '', company: 'RGA', project: '', contact: '', active: true,
    schedulingMode: 'rotation', pattern: ['A', 'B', 'C'], anchorDate: anchorForMonth(month), vacations: []
  });
  const [preset, setPreset] = useState(() => schedulePreset(form));
  const [customPattern, setCustomPattern] = useState(() => form.pattern.join('/'));
  const [error, setError] = useState('');
  const change = (field, value) => { setForm((old) => ({ ...old, [field]: value })); onDirtyChange(true); };
  const submit = async (event) => {
    event.preventDefault();
    const project = Number(String(form.project || '').replace(/[^0-9]/g, ''));
    if (form.active && ![8, 14, 20, 30].includes(project)) { setError('Choose FM008, FM014, FM020 or FM030 as the employee project.'); return; }
    const pattern = preset === 'custom' ? customPattern.trim().toUpperCase().split(/[,\s/]+/) : preset.split('/');
    if (pattern.length < 1 || pattern.length > 52 || pattern.some(code => !['A', 'B', 'C'].includes(code))) {
      setError('Enter 1–52 weekly shifts using A, B or C, separated by /, commas or spaces.'); return;
    }
    if (new Date(`${form.anchorDate}T00:00:00Z`).getUTCDay() !== 6) { setError('The rotation start date must be a Saturday.'); return; }
    const schedulingMode = preset === 'custom' ? 'custom' : pattern.length === 1 ? 'fixed' : 'rotation';
    setError('');
    await onSave({ ...form, pattern, schedulingMode });
  };
  return <form onSubmit={submit} className="space-y-4">
    {error && <Alert variant="error">{error}</Alert>}
    <fieldset disabled={busy} className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {['name', 'designation', 'project', 'contact'].map((field) => FIELD_OPTIONS[field] ? (
          <Select key={field} label={field.charAt(0).toUpperCase() + field.slice(1)} value={form[field] || ''}
            onChange={(event) => change(field, event.target.value)}>
            <option value="">Select a {field}</option>
            {FIELD_OPTIONS[field].map((value) => <option key={value} value={value}>{value}</option>)}
            {form[field] && !FIELD_OPTIONS[field].includes(form[field]) && (
              <option value={form[field]} disabled>{form[field]} (current)</option>
            )}
          </Select>
        ) : <Input key={field}
          label={field === 'name' ? 'Employee name' : field.charAt(0).toUpperCase() + field.slice(1)}
          value={form[field] || ''} maxLength={field === 'contact' ? 40 : 100} required={field === 'name'}
          onChange={(event) => change(field, event.target.value)} />)}
        <Select label="Scheduling" value={preset}
          onChange={(event) => { setPreset(event.target.value); onDirtyChange(true); }}>
          {SCHEDULE_PRESETS.map(value => <option key={value} value={value}>Fixed {value}</option>)}
          <option value="custom">Custom</option>
        </Select>
        {preset === 'custom' && <Input label="Custom weekly pattern" value={customPattern} required
          onChange={(event) => { setCustomPattern(event.target.value); onDirtyChange(true); }} placeholder="A/A/B/C" />}
        <Input label="Rotation start date (Saturday)" type="date" required min="2000-01-01" max="2100-12-31"
          value={form.anchorDate} onChange={(event) => change('anchorDate', event.target.value)} />
      </div>
      <p className="text-xs text-muted-fg">Each employee follows their own repeating weekly pattern. The first shift applies to the rotation start week; each Saturday advances one step. Fixed A/B alternates A then B; Fixed A/B/C repeats A, B then C. Custom supports sequences such as A/A/B/C. Coverage never changes these assignments.</p>
      <Checkbox label="Include in generated rosters" checked={form.active} onChange={(event) => change('active', event.target.checked)} />
      <div className="space-y-3">
        <h4 className="text-sm font-semibold">Vacations</h4>
        <p className="text-xs text-muted-fg">Periods may span months. Fridays stay OC; vacations remove the employee from available coverage.</p>
        {form.vacations.map((vacation, index) => <div key={index} className="flex flex-wrap items-end gap-3">
          {['start', 'end'].map((field) => <Input key={field} className="sm:!w-48" label={`Vacation ${index + 1} ${field}`} type="date" required
            min={field === 'end' ? vacation.start || '2000-01-01' : '2000-01-01'} max="2100-12-31" value={vacation[field]}
            onChange={(event) => change('vacations', form.vacations.map((entry, i) => i === index ? { ...entry, [field]: event.target.value } : entry))} />)}
          <Button variant="ghost" onClick={() => change('vacations', form.vacations.filter((_, i) => i !== index))}>Remove vacation {index + 1}</Button>
        </div>)}
        <Button variant="secondary" onClick={() => change('vacations', [...form.vacations, { start: '', end: '' }])} disabled={form.vacations.length >= 100}>Add vacation</Button>
      </div>
      <Button type="submit" loading={busy}>Save employee setup</Button>
    </fieldset>
  </form>;
}
